import { useId, useMemo, useState, type ReactNode } from 'react';
import { Button, Form, InputGroup, Nav } from 'react-bootstrap';
import { Check2, Search, X } from 'react-bootstrap-icons';
import { Client } from '@/types';
import { ClientTags } from '@/components/clientFlags/ClientTags';
import { useClientFlags } from '@/hooks/useClientFlags';
import { groupClientsByType } from '@/services/clientService';
import './tools/clientPicker.css';

interface ClientSelectGroupProps {
  value: string;
  onChange: (value: string) => void;
  clients: Client[];
  disabled?: boolean;
  /** The client list is still being fetched. The empty list then says so. */
  loading?: boolean;
  showCounts?: boolean;
  deploymentCounts?: Record<string, number>;
  size?: 'sm' | 'lg';
  className?: string;
  style?: React.CSSProperties;
  /**
   * When provided, renders a persistent, selectable "all" row at the top of the
   * list and makes the clear (×) action reset to this value instead of ''. Use
   * in filter contexts (e.g. Deployments history) where "all" is a real
   * selection, not an empty one — otherwise clearing strands the filter on a
   * value that matches nothing, with no way to restore it (BUG-078).
   */
  allOption?: { value: string; label: string };
}

type CustomerScope = 'active' | 'all';

/**
 * Pick one client. The list is split into dev stacks and customer stacks.
 * Customer stacks marked inactive are left out until "All customers" is
 * chosen, and the dev stacks can be hidden to shorten the list. The client
 * that is chosen stays in the list whatever the filters say.
 *
 * The inactive and do-not-deploy tags come from the shared tag reader, so a
 * caller passes nothing for them. Until the tags are read every client shows.
 */
export function ClientSelectGroup({
  value,
  onChange,
  clients,
  disabled = false,
  loading = false,
  showCounts = false,
  deploymentCounts = {},
  size,
  className,
  style,
  allOption,
}: ClientSelectGroupProps) {
  const flags = useClientFlags();
  const [searchTerm, setSearchTerm] = useState('');
  const [showDev, setShowDev] = useState(true);
  const [scope, setScope] = useState<CustomerScope>('active');
  // Two pickers can sit on one page, so each gets its own ids.
  const uid = useId();

  const { devClients, productionClients } = groupClientsByType(clients);
  const activeCustomers = productionClients.filter((c) => !flags.isInactive(c.name));

  const lowerSearch = searchTerm.toLowerCase();
  const applySearch = (list: Client[]) =>
    searchTerm ? list.filter((c) => c.name.toLowerCase().includes(lowerSearch)) : list;

  // The chosen client is never filtered out of the list: a person must see what is selected.
  const filteredDev = applySearch(devClients.filter((c) => showDev || c.name === value));
  const filteredProd = applySearch(
    productionClients.filter((c) => scope === 'all' || !flags.isInactive(c.name) || c.name === value)
  );
  const hasResults = filteredDev.length > 0 || filteredProd.length > 0;
  // Inactive customers the search matches while they are filtered out. The empty list offers to show them.
  const hiddenMatches =
    scope === 'active' && searchTerm
      ? applySearch(productionClients).filter((c) => flags.isInactive(c.name) && c.name !== value).length
      : 0;

  const isDevSelected = useMemo(() => devClients.some((c) => c.name === value), [devClients, value]);

  // Clearing the selection resets to the "all" sentinel when one is configured,
  // otherwise to an empty string (the deselect behaviour used by the deploy form
  // and config tools).
  const clearValue = allOption?.value ?? '';

  /** One selectable row. `rowValue` is what the picker reports when it is chosen. */
  const renderRow = (rowValue: string, label: string, extraClass = '', tags?: (selected: boolean) => ReactNode) => {
    const selected = value === rowValue;
    return (
      <div
        key={rowValue}
        role="button"
        aria-pressed={selected}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onClick={() => {
          if (!disabled) onChange(rowValue);
        }}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onChange(rowValue);
          }
        }}
        className={[
          'client-pick__row',
          extraClass,
          selected ? 'client-pick__row--selected' : '',
          disabled ? 'client-pick__row--disabled' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <span className="client-pick__row-name">{label}</span>
        {tags?.(selected)}
        {selected && <Check2 className="client-pick__row-tick" aria-hidden="true" />}
      </div>
    );
  };

  const renderClientRow = (client: Client) => {
    const count =
      showCounts && deploymentCounts[client.name] !== undefined ? ` (${deploymentCounts[client.name]})` : '';
    return renderRow(client.name, `${client.name}${count}`, '', (selected) => (
      <ClientTags clientName={client.name} flags={flags} onSelected={selected} />
    ));
  };

  const listClass = ['client-pick__list', 'client-pick__list--fixed', size === 'sm' ? 'client-pick__list--sm' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={['client-pick', className].filter(Boolean).join(' ')} style={style}>
      <div className="client-pick__groups">
        <span className="client-pick__groups-label">Show</span>
        <Nav
          variant="pills"
          className="nav-pills-custom client-pick__scope"
          activeKey={scope}
          onSelect={(key) => key && setScope(key as CustomerScope)}
          aria-label="Which customer stacks to list"
        >
          <Nav.Item>
            <Nav.Link eventKey="active" disabled={disabled}>
              Active customers<span className="client-pick__count">{activeCustomers.length}</span>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="all" disabled={disabled}>
              All customers<span className="client-pick__count">{productionClients.length}</span>
            </Nav.Link>
          </Nav.Item>
        </Nav>
        <Form.Check
          type="checkbox"
          id={`${uid}-show-dev`}
          checked={showDev}
          onChange={(e) => setShowDev(e.currentTarget.checked)}
          disabled={disabled || devClients.length === 0}
          label={
            <span>
              Dev stacks<span className="client-pick__count">{devClients.length}</span>
            </span>
          }
        />
      </div>

      <InputGroup size="sm" className="client-pick__search">
        <InputGroup.Text>
          <Search size={14} />
        </InputGroup.Text>
        <Form.Control
          type="text"
          placeholder="Search clients"
          aria-label="Search clients"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            // Enter in a search box must not send the form the picker sits in.
            if (e.key === 'Enter') e.preventDefault();
          }}
          disabled={disabled}
        />
        {searchTerm && (
          <Button
            variant="outline-secondary"
            onClick={() => setSearchTerm('')}
            disabled={disabled}
            aria-label="Clear the search"
          >
            <X size={14} />
          </Button>
        )}
      </InputGroup>

      <div className={listClass}>
        {allOption && !searchTerm && renderRow(allOption.value, allOption.label, 'client-pick__row--all')}

        {filteredDev.length > 0 && (
          <>
            <div className="client-pick__group-label">
              <span>Dev stacks</span>
              <span>{filteredDev.length}</span>
            </div>
            {filteredDev.map(renderClientRow)}
          </>
        )}

        {filteredProd.length > 0 && (
          <>
            <div className="client-pick__group-label">
              <span>Customer stacks</span>
              <span>{filteredProd.length}</span>
            </div>
            {filteredProd.map(renderClientRow)}
          </>
        )}

        {!hasResults && (
          <div className="client-pick__empty">
            {loading ? (
              'Loading clients'
            ) : searchTerm ? (
              hiddenMatches > 0 ? (
                <>
                  No active client matches "{searchTerm}". {hiddenMatches}{' '}
                  {hiddenMatches === 1 ? 'inactive customer does' : 'inactive customers do'}.
                  <Button variant="link" size="sm" className="client-pick__show-all" onClick={() => setScope('all')}>
                    Search all customers
                  </Button>
                </>
              ) : (
                `No client matches "${searchTerm}".`
              )
            ) : (
              'There are no clients to pick from.'
            )}
          </div>
        )}
      </div>

      {/* The chosen client, under the list. Clicking it clears the choice. */}
      {value && value !== allOption?.value && (
        <div className="client-pick__chosen">
          <span>Selected</span>
          <button
            type="button"
            className="client-pick__chip"
            onClick={() => onChange(clearValue)}
            disabled={disabled}
            title="Clear the selection"
          >
            {value}
            {isDevSelected && <span className="client-pick__chip-tag">dev</span>}
            <X size={12} aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}
